import { useState, useEffect, useCallback } from 'react';
import { Key, Globe, Search, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import type { ADPasswordPolicy } from './adTypes';
import baseapi from '../../services/baseapi';
import { Pagination } from '../InternalScan/InternalScanDashboard/Pagination';

interface ADPasswordPolicyViewProps {
    policies?: ADPasswordPolicy[];
    asset_id?: string | number;
}

export default function ADPasswordPolicyView({ policies: initialPolicies, asset_id: propAssetId }: ADPasswordPolicyViewProps) {
    const info = JSON.parse(sessionStorage.getItem('internal_ids') || '{}');
    const asset_id = propAssetId || info.asset_id;

    const [policies, setPolicies] = useState<ADPasswordPolicy[]>(initialPolicies || []);
    const [totalRecords, setTotalRecords] = useState<number>(initialPolicies?.length || 0);
    const [loading, setLoading] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Debounce search input
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setCurrentPage(1);
        }, 350);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    // Fetch API with skip & limit
    const fetchPolicies = useCallback(async () => {
        setLoading(true);
        try {
            const queryObj: any = { is_deleted: false };
            if (asset_id) queryObj['asset_id'] = asset_id;

            if (debouncedSearch.trim()) {
                queryObj.$or = [
                    { name: { $ilike: `%${debouncedSearch.trim()}%` } },
                    { domain: { $ilike: `%${debouncedSearch.trim()}%` } },
                    { distinguished_name: { $ilike: `%${debouncedSearch.trim()}%` } },
                ];
            }

            const params: any = {
                query: JSON.stringify(queryObj),
                skip: (currentPage - 1) * itemsPerPage,
                limit: itemsPerPage,
            };

            const response = await baseapi.get('/ad_password_policies', { params });
            if (response.data && response.data.status) {
                setPolicies(response.data.data || []);
                setTotalRecords(response.data.total ?? response.data.data?.length ?? 0);
            } else if (response.data?.data) {
                setPolicies(response.data.data);
                setTotalRecords(response.data.total ?? response.data.data.length);
            }
        } catch (error: any) {
            console.error('Failed to fetch password policies:', error);
        } finally {
            setLoading(false);
        }
    }, [asset_id, debouncedSearch, currentPage, itemsPerPage]);

    useEffect(() => {
        fetchPolicies();
    }, [fetchPolicies]);

    const totalPages = Math.ceil(totalRecords / itemsPerPage) || 1;

    const formatLockoutDuration = (val?: string | number) => {
        if (!val && val !== 0) return '—';
        if (val === '0' || val === 0) return 'Until Unlocked';
        const str = String(val);
        const match = str.match(/(\d+):(\d+):(\d+)/);
        if (!match) return str;
        const [, h, m] = match;
        if (h === '00') return `${parseInt(m, 10)} mins`;
        return `${parseInt(h, 10)}h ${parseInt(m, 10)}m`;
    };

    return (
        <div className="w-full bg-gray-900/80 border border-gray-800 rounded-xl overflow-hidden shadow-2xl flex flex-col">
            {/* Header / Toolbar */}
            <div className="flex items-center justify-between p-3 bg-gray-900/90 border-b border-gray-800 backdrop-blur-md">
                <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-orange-500/20 border border-orange-500/30 rounded-md">
                        <Key className="text-orange-400 w-4 h-4" />
                    </div>
                    <div>
                        <h2 className="text-xs font-bold text-white tracking-wide">Domain Password Policies</h2>
                        <span className="text-[9px] text-gray-200 uppercase tracking-widest font-mono">
                            {totalRecords} {totalRecords === 1 ? 'Policy' : 'Policies'} Detected
                        </span>
                    </div>
                </div>

                <div className="relative w-56 group">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-200 group-focus-within:text-orange-400 transition-colors" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search policies or domains..."
                        className="w-full bg-gray-900 border border-gray-800 rounded-md pl-8 pr-3 py-1 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-orange-500/60 transition-all"
                    />
                </div>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-x-auto custom-scroll">
                <table className="w-full text-left text-xs border-collapse">
                    <thead className="bg-gray-900 border-b border-gray-800 sticky top-0 z-10">
                        <tr className="text-gray-200 uppercase text-[9px] tracking-widest font-bold">
                            <th className="p-2.5">Policy Name</th>
                            <th className="p-2.5">Domain</th>
                            <th className="p-2.5">Min Length</th>
                            <th className="p-2.5">Complexity</th>
                            <th className="p-2.5">History</th>
                            <th className="p-2.5">Max / Min Age</th>
                            <th className="p-2.5">Lockout Threshold</th>
                            <th className="p-2.5">Lockout Duration</th>
                            <th className="p-2.5">Reversible Encryption</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                        {loading ? (
                            <tr>
                                <td colSpan={9} className="py-16 text-center text-gray-400 font-mono">
                                    <div className="flex items-center justify-center gap-2">
                                        <Loader2 className="w-4 h-4 animate-spin text-orange-400" />
                                        <span>Loading Password Policies...</span>
                                    </div>
                                </td>
                            </tr>
                        ) : policies.length === 0 ? (
                            <tr>
                                <td colSpan={9} className="py-16 text-center text-gray-500 font-mono">
                                    No Password Policies found.
                                </td>
                            </tr>
                        ) : (
                            policies.map((policy, idx) => {
                                const minLength = policy.min_pwd_length ?? 0;
                                const complexity = policy.complexity_enabled;
                                const reversible = policy.reversible_encryption_enabled;
                                const threshold = policy.lockout_threshold;

                                return (
                                    <tr key={policy.object_guid || idx} className="hover:bg-gray-900/60 transition-colors group">
                                        <td className="p-2.5 font-bold text-white max-w-xs">
                                            <div className="flex items-center gap-2">
                                                <Key size={13} className="text-orange-400 shrink-0" />
                                                <div>
                                                    <span className="block truncate">{policy.name || 'Default Domain Policy'}</span>
                                                    <span className="text-[9px] text-gray-500 font-mono block truncate">
                                                        {policy.distinguished_name || policy.name || '—'}
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-2.5 font-mono text-gray-300">
                                            <div className="flex items-center gap-1">
                                                <Globe size={11} className="text-blue-400" />
                                                {policy.domain || '—'}
                                            </div>
                                        </td>
                                        <td className="p-2.5">
                                            <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold border ${
                                                minLength >= 12
                                                    ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                                                    : minLength >= 8
                                                    ? 'bg-amber-500/20 text-amber-300 border-amber-500/30'
                                                    : 'bg-red-500/20 text-red-300 border-red-500/30'
                                            }`}>
                                                {minLength} chars
                                            </span>
                                        </td>
                                        <td className="p-2.5">
                                            {complexity ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                                                    <CheckCircle2 size={11} /> Enabled
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold bg-red-500/20 text-red-300 border border-red-500/30">
                                                    <XCircle size={11} /> Disabled
                                                </span>
                                            )}
                                        </td>
                                        <td className="p-2.5 font-mono text-gray-200">
                                            {policy.pwd_history_length ? `${policy.pwd_history_length} remembered` : '—'}
                                        </td>
                                        <td className="p-2.5 font-mono text-gray-200">
                                            <div className="flex items-center gap-1">
                                                <span>{policy.max_pwd_age ? `${policy.max_pwd_age}d` : 'Never'}</span>
                                                <span className="text-gray-500">/</span>
                                                <span className="text-gray-400">{policy.min_pwd_age ? `${policy.min_pwd_age}d` : '0d'}</span>
                                            </div>
                                        </td>
                                        <td className="p-2.5 font-mono">
                                            <span className={`font-bold ${
                                                threshold && parseInt(String(threshold), 10) > 0
                                                    ? 'text-emerald-400'
                                                    : 'text-amber-400'
                                            }`}>
                                                {threshold && parseInt(String(threshold), 10) > 0
                                                    ? `${threshold} attempts`
                                                    : 'Not Configured'}
                                            </span>
                                        </td>
                                        <td className="p-2.5 font-mono text-gray-200">
                                            {formatLockoutDuration(policy.lockout_duration)}
                                        </td>
                                        <td className="p-2.5">
                                            {!reversible ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                                                    <CheckCircle2 size={11} /> Disabled
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold bg-red-500/20 text-red-300 border border-red-500/30">
                                                    <XCircle size={11} /> Enabled (Weak)
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination Controls */}
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
                totalItems={totalRecords}
                onItemsPerPageChange={(n) => {
                    setItemsPerPage(n);
                    setCurrentPage(1);
                }}
                itemsPerPageOptions={[5, 10, 25, 50]}
            />
        </div>
    );
}
